import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Form, notification, Spin } from 'antd'

import { Drawer, DrawerFooter } from '@/components/ui/drawer'
import { Button } from '@/components/ui/button'
import { ConfirmModal } from '@/components/ui/confirm-modal'
import { MailForm } from '@/components/form/MailForm'
import type { MailFormValues } from '@/components/form/MailForm'
import { createMail, updateMail, getMailById, encryptMailPassword, testMailConnection } from '@/api/mails.api'
import type { MailWriteInput } from '@/api/mails.api'
import { currentUserAtom } from '@/store/auth.atom'
import { useAtomValue } from 'jotai'

interface MailFormDrawerProps {
    isOpen: boolean
    mode: 'create' | 'update'
    mailId: string | null
    onClose: () => void
}

export function MailFormDrawer({ isOpen, mode, mailId, onClose }: MailFormDrawerProps) {
    const [form] = Form.useForm<MailFormValues>()
    const queryClient = useQueryClient()
    const currentUser = useAtomValue(currentUserAtom)
    const showUnitColumn = currentUser?.userType === 1
    const [testResult, setTestResult] = useState<{ status: 'success' | 'error', host?: string, message?: string } | null>(null)
    const [isTesting, setIsTesting] = useState(false)

    const { data: mail, isLoading: isFetchingMail } = useQuery({
        queryKey: ['mail-detail', mailId],
        queryFn: ({ signal }) => mailId ? getMailById(mailId, signal) : Promise.resolve(null),
        enabled: isOpen && mode === 'update' && !!mailId,
        refetchOnMount: 'always',
        staleTime: 0,
        gcTime: 0,
    })

    useEffect(() => {
        if (isOpen) {
            if (mode === 'update' && mail) {
                form.setFieldsValue({
                    id: mail.id,
                    unitId: mail.unitId,
                    unitName: mail.unitName,
                    serverUrl: mail.serverUrl,
                    serverPort: mail.serverPort,
                    protocol: mail.protocol,
                    mail: mail.mail,
                    name: mail.name,
                    username: mail.username,
                    password: mail.password ? '******' : '',
                })
            } else if (mode === 'create') {
                form.resetFields()
            }
        } else {
            form.resetFields()
        }
    }, [isOpen, mode, mail, form])

    const createMutation = useMutation({
        mutationFn: createMail,
        onSuccess: (response: any) => {
            notification.success({ message: response.responseMessage || 'Thêm mới mail server thành công!' })
            queryClient.invalidateQueries({ queryKey: ['mails'] })
            onClose()
        },
    })

    const updateMutation = useMutation({
        mutationFn: (values: MailWriteInput) => {
            if (!mailId) throw new Error('Missing mail ID')
            return updateMail(mailId, values)
        },
        onSuccess: (response: any) => {
            notification.success({ message: response.responseMessage || 'Cập nhật mail server thành công!' })
            queryClient.invalidateQueries({ queryKey: ['mails'] })
            onClose()
        },
    })

    const handleTestConnection = async () => {
        try {
            setIsTesting(true)
            const values = await form.validateFields()
            let finalPassword = values.password

            if (mode === 'update' && (!finalPassword || finalPassword === '******')) {
                finalPassword = mail?.password
            } else if (finalPassword) {
                finalPassword = await encryptMailPassword(finalPassword)
            }

            await testMailConnection({
                serverUrl: values.serverUrl,
                serverPort: values.serverPort,
                protocol: values.protocol,
                username: values.username,
                password: finalPassword,
            })

            setTestResult({ status: 'success', host: values.serverUrl })
        } catch (error: any) {
            if (error && error.errorFields) return
            setTestResult({ status: 'error', message: error?.message || '{Lỗi do hệ thống trả về}' })
        } finally {
            setIsTesting(false) 
        }
    }

    const isSubmitting = createMutation.isPending || updateMutation.isPending
    const isDrawerDisabled = isSubmitting || isFetchingMail

    const handleSubmit = async (values: MailFormValues) => {
        let finalPassword = values.password
            
        if (mode === 'update' && (!finalPassword || finalPassword === '******')) {
            finalPassword = mail?.password
        } else if (finalPassword) {
            finalPassword = await encryptMailPassword(finalPassword)
        }

        const payload: MailWriteInput = {
            unitId: showUnitColumn ? values.unitId : currentUser?.unitId,
            serverUrl: values.serverUrl,
            serverPort: values.serverPort,
            protocol: values.protocol,
            mail: values.mail,
            name: values.name,
            username: values.username,
            ...(finalPassword ? { password: finalPassword } : {}),
            ...(mode === 'update' ? { status: mail?.status ?? 1 } : {}),
        }

        if (mode === 'create') {
            createMutation.mutate(payload)
        } else {
            updateMutation.mutate(payload)
        }
    }

    return (
        <>
            <Drawer
                open={isOpen}
                onClose={onClose}
                title={mode === 'create' ? 'THÊM MỚI MAIL SERVER' : 'CẬP NHẬT MAIL SERVER'}
                styles={{ body: { overflow: 'auto' } }}
                footer={
                    <DrawerFooter>
                        <Button variant="outline" size="md" onClick={onClose} disabled={isDrawerDisabled || isTesting} className="min-w-[120px] h-10">
                            Hủy
                        </Button>
                        <Button
                            variant="outline"
                            size="md"
                            onClick={handleTestConnection}
                            disabled={isDrawerDisabled || isTesting}
                            className="min-w-[150px] h-10 border-[#CE7A58] text-[#CE7A58] hover:bg-[#CE7A58]/10"
                        >
                            Kiểm tra kết nối
                        </Button>
                        <Button variant="primary" size="md" onClick={() => form.submit()} disabled={isDrawerDisabled || isTesting} className="min-w-[120px] h-10 bg-[#CE7A58] hover:bg-[#b8694a]">
                            {isSubmitting ? 'Đang lưu...' : 'Lưu'}
                        </Button>
                    </DrawerFooter>
                }
            >
                <Spin spinning={isFetchingMail} description="Đang tải dữ liệu...">
                    <div className="min-h-[300px]">
                        {(mode === 'create' || mail) && <MailForm form={form} mode={mode} showUnitColumn={showUnitColumn} onSubmit={handleSubmit} />}
                    </div>
                </Spin>
            </Drawer>

            <ConfirmModal
                isOpen={testResult?.status === 'success'}
                title="Kết nối thành công"
                description={`Kết nối đến Mail Server {${testResult?.host || ''}} thành công.`}
                variant="success"
                onClose={() => setTestResult(null)}
                cancelText="Đóng"
            />

            <ConfirmModal
                isOpen={testResult?.status === 'error'}
                title="Kết nối thất bại"
                description={testResult?.message || '{Lỗi do hệ thống trả về}'}
                variant="danger"
                onClose={() => setTestResult(null)}
                cancelText="Đóng"
            />
        </>
    )
}